/**
 * One map from audit action codes to a plain sentence.
 * An action that is not in this map is "Change recorded" plus a name,
 * and the code itself is never shown.
 */

const ACTION_SENTENCES: Record<string, string> = {
  // ONE-TRUTH-FE-2: {place} already carries its article ("a risk"); "created a {place}" read "created a a risk".
  create: "created {place}",
  update: "updated {place}",
  POLICY_CREATED: "created a governance rule in {place}",
  POLICY_UPDATED: "changed governance rules in {place}",
  delete: "deleted {place}",
  activate: "activated {place}",
  archive: "archived {place}",
  compute: "ran a calculation for {place}",
  attach: "attached something to {place}",
  detach: "detached something from {place}",
  invite: "invited someone to {place}",
  accept: "accepted an invitation to {place}",
  suspend: "suspended access in {place}",
  reinstate: "restored access in {place}",
  upload_complete: "finished an upload in {place}",
  download_link: "created a download link in {place}",
  presign_create: "prepared an upload in {place}",
  quota_block: "blocked an action because a limit was reached in {place}",
  plan_status_block: "blocked an action because of the plan in {place}",
  wip_override: "overrode a work-in-progress limit in {place}",
  role_change: "changed a role in {place}",
  user_registered: "registered an account in {place}",
  org_created: "created the organization {place}",
  email_verification_sent: "sent a verification email in {place}",
  email_verified: "verified an email in {place}",
  email_verification_bypassed: "bypassed email verification in {place}",
  resend_verification: "resent a verification email in {place}",
  password_reset_requested: "requested a password reset in {place}",
  password_reset_completed: "completed a password reset in {place}",
  password_reset_link_generated: "generated a password reset link in {place}",
  governance_evaluate: "changed governance rules in {place}",
  soft_remove_to_trash: "moved {place} to trash",
  restore_from_trash: "restored {place} from trash",
  retention_purge_batch: "permanently removed expired items from {place}",
  permanent_delete_from_trash: "permanently deleted {place}",
  guard_allow: "allowed an action in {place}",
  guard_deny: "denied an action in {place}",
  TEMPLATE_APPLIED: "applied a template in {place}",
  TEMPLATE_APPLY_FAILED: "could not apply a template in {place}",
  GATE_DECIDE: "recorded a gate decision in {place}",
  GATE_DECIDE_BLOCKED: "blocked a gate decision in {place}",
  DOC_TRANSITION: "moved a document forward in {place}",
  DOCUMENT_TRANSITION_FAILED: "could not move a document in {place}",
  complexity_mode_changed: "changed how {place} is set up",
  plan_changed: "changed the plan for {place}",
  GATE_SUBMITTED: "submitted a gate in {place}",
  PHASE_CREATED: "created a phase in {place}",
  PHASE_REORDERED: "reordered phases in {place}",
  PHASE_RESTORED: "restored a phase in {place}",
  PHASE_DELETED: "deleted a phase in {place}",
  ACK_CONSUMED: "recorded an acknowledgment in {place}",
  PHASE_UPDATED_WITH_ACK: "updated a phase in {place}",
  AUTH_RATE_LIMIT_DEGRADED: "recorded that sign-in limits stopped working in {place}",
  AUTH_RATE_LIMIT_RECOVERED: "recorded that sign-in limits recovered in {place}",
  AUDIT_WRITE_RECOVERED: "recorded that the audit trail recovered in {place}",
  csv_import_executed: "imported a spreadsheet into {place}",
  GOVERNANCE_CONTRACT_CREATED: "created governance rules in {place}",
  GOVERNANCE_CONTRACT_UPGRADED: "updated governance rules in {place}",
};

const BY_ACTION = new Map<string, string>();
for (const [code, sentence] of Object.entries(ACTION_SENTENCES)) {
  BY_ACTION.set(code, sentence);
  BY_ACTION.set(code.toLowerCase(), sentence);
}

const ENTITY_LABELS: Record<string, string> = {
  organization: "the organization",
  workspace: "the workspace",
  policy: "a governance rule",
  project: "the project",
  portfolio: "the portfolio",
  program: "the program",
  work_task: "a task",
  work_risk: "a risk",
  doc: "a document",
  attachment: "a file",
  scenario_plan: "a scenario",
  scenario_action: "a scenario",
  scenario_result: "a scenario",
  baseline: "a baseline",
  capacity_calendar: "the capacity calendar",
  billing_plan: "the plan",
  entitlement: "an entitlement",
  webhook: "a webhook",
  board_move: "a board",
  user: "a user",
  email_verification: "an email",
  password_reset: "a password reset",
  authorization_decision: "an access decision",
  template_lineage: "a template",
  gate_approval: "a gate",
  document_instance: "a document",
  project_artifact: "an artifact",
  project_artifact_item: "an artifact",
  phase_gate_submission: "a gate",
  template: "a template",
  phase: "a phase",
  security: "security",
  import_job: "an import",
};

function entityName(entityType?: string | null): string {
  const raw = (entityType || "").trim();
  if (!raw) return "this record";
  const known = ENTITY_LABELS[raw.toLowerCase()];
  if (known) return known;
  return raw.replace(/_/g, " ").toLowerCase();
}

function placeNameOf(
  entityType?: string | null,
  placeName?: string | null,
): string {
  const named = placeName?.trim();
  if (named) return named;
  return entityName(entityType);
}

export function describeAuditEvent(input: {
  actorName?: string | null;
  action: string;
  entityType?: string | null;
  placeName?: string | null;
}): string {
  const actor = input.actorName?.trim() || "Someone";
  const place = placeNameOf(input.entityType, input.placeName);
  const template = BY_ACTION.get(input.action.trim()) ?? BY_ACTION.get(input.action.trim().toLowerCase());
  if (!template) {
    return `Change recorded for ${place}`;
  }
  // ONE-TRUTH-FE-2: with no named place, an action that already names its object
  // ("deleted a phase in {place}") must not repeat it ("… in a phase").
  const unnamed = !input.placeName?.trim();
  if (unnamed && template.includes(" in {place}") && template.replace(" in {place}", "").endsWith(` ${place}`)) {
    return `${actor} ${template.replace(" in {place}", "")}`;
  }
  return `${actor} ${template.replaceAll("{place}", place)}`;
}

const EXCEPTION_TYPE_LABELS: Record<string, string> = {
  GOVERNANCE_RULE: "Governance rule exception",
  BUDGET: "Budget exception",
  CAPACITY: "Capacity exception",
  PHASE_GATE: "Phase gate exception",
  OWNER_ASSIGNMENT: "Owner assignment exception",
};

const EXCEPTION_STATUS_LABELS: Record<string, string> = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  NEEDS_INFO: "Needs information",
  CONSUMED: "Used",
};

/**
 * ONE-TRUTH-FE-2: a governance-exception activity row as people read it, from the
 * structured exceptionType and status (governance-exception.entity.ts). The
 * backend's free-text description and reason are never parsed or shown raw
 * (GOV-REASON-LABELS-1 separates code from text server-side).
 */
export function describeGovernanceActivity(input: {
  exceptionType?: string | null;
  status?: string | null;
}): string {
  const type = EXCEPTION_TYPE_LABELS[(input.exceptionType || "").trim().toUpperCase()] ?? "Governance exception";
  const status = EXCEPTION_STATUS_LABELS[(input.status || "").trim().toUpperCase()] ?? "Status not recognized";
  return `${type} · ${status}`;
}
